import React, { useState } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
} from '@mui/material';
import {
  MoreVert,
  Visibility,
  VisibilityOff,
  Delete,
  ContentCopy,
  Refresh,
} from '@mui/icons-material';
import { WidgetConfig, WidgetType } from '../../types/widget';
import SceneAnalysisWidget from './SceneAnalysisWidget';
import DriftAnalysisWidget from './DriftAnalysisWidget';
import BudgetTrackerWidget from './BudgetTrackerWidget';
import DelayAnalysisWidget from './DelayAnalysisWidget';
import ProductionDailyFocusWidget from './ProductionDailyFocusWidget';
import TimingTowerWidget from './TimingTowerWidget';
import ProductionAssumptionsWidget from './ProductionAssumptionsWidget';
import DocumentStatusWidget from './DocumentStatusWidget';
import DocumentLibraryWidget from './DocumentLibraryWidget';
import ProductionVelocityWidget from './ProductionVelocityWidget';

interface WidgetContainerProps {
  widget: WidgetConfig;
  onUpdate: (widget: WidgetConfig) => void;
  onDelete: (widgetId: string) => void;
  onDuplicate: (widget: WidgetConfig) => void;
}

const WidgetContainer: React.FC<WidgetContainerProps> = ({
  widget,
  onUpdate,
  onDelete,
  onDuplicate,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleToggleVisibility = () => {
    onUpdate({
      ...widget,
      isVisible: !widget.isVisible,
      updatedAt: new Date(),
    });
    handleMenuClose();
  };

  const handleRefresh = () => {
    // Trigger a refresh by updating the widget
    onUpdate({
      ...widget,
      updatedAt: new Date(),
    });
    handleMenuClose();
  };

  const handleDuplicate = () => {
    onDuplicate(widget);
    handleMenuClose();
  };

  const handleDelete = () => {
    onDelete(widget.id);
    handleMenuClose();
  };

  const renderWidgetContent = () => {
    switch (widget.type) {
      case WidgetType.SCENE_ANALYSIS:
        return <SceneAnalysisWidget widgetId={widget.id} title={widget.title} />;
      case WidgetType.DRIFT_ANALYSIS:
        return <DriftAnalysisWidget widgetId={widget.id} title={widget.title} />;
      case WidgetType.BUDGET_TRACKING:
        return <BudgetTrackerWidget widgetId={widget.id} title={widget.title} />;
      case WidgetType.DELAY_ANALYSIS:
        return <DelayAnalysisWidget widgetId={widget.id} title={widget.title} />;
      case WidgetType.PRODUCTION_DAILY_FOCUS:
        return <ProductionDailyFocusWidget widgetId={widget.id} title={widget.title} />;
      case WidgetType.TIMING_TOWER:
        return <TimingTowerWidget />;
      case WidgetType.PRODUCTION_ASSUMPTIONS:
        return <ProductionAssumptionsWidget widgetId={widget.id} title={widget.title} />;
      case WidgetType.DOCUMENT_STATUS:
        return <DocumentStatusWidget />;
      case WidgetType.DOCUMENT_LIBRARY:
        return <DocumentLibraryWidget widgetId={widget.id} title={widget.title} />;
      case WidgetType.PRODUCTION_VELOCITY:
        return <ProductionVelocityWidget widgetId={widget.id} title={widget.title} />;
      default:
        return (
          <Paper
            sx={{
              p: 3,
              textAlign: 'center',
              backgroundColor: 'background.paper',
              border: '2px dashed #4A5568',
              borderRadius: '12px',
            }}
          >
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {widget.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Widget type: {widget.type}
            </Typography>
          </Paper>
        );
    }
  };

  if (!widget.isVisible) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        border: isHovered ? '2px solid #4299E1' : 'none',
        borderRadius: '12px',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        display: 'flex',
        flexDirection: 'column',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Widget Menu Button */}
      {isHovered && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1000,
          }}
        >
          <IconButton
            size="small"
            onClick={handleMenuClick}
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
              },
            }}
          >
            <MoreVert />
          </IconButton>
        </Box>
      )}

      {/* Widget Content */}
      <Box
        sx={{
          flex: 1,
          width: '100%',
          height: '100%',
          overflow: 'scroll',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0, // Important for flex child to shrink
        }}
      >
        {renderWidgetContent()}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: '#2D3748',
            color: 'white',
            '& .MuiMenuItem-root': {
              color: 'white',
              '&:hover': {
                backgroundColor: '#4A5568',
              },
            },
          },
        }}
      >
        <MenuItem onClick={handleToggleVisibility}>
          <ListItemIcon>
            {widget.isVisible ? <VisibilityOff /> : <Visibility />}
          </ListItemIcon>
          <ListItemText>
            {widget.isVisible ? 'Hide Widget' : 'Show Widget'}
          </ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleRefresh}>
          <ListItemIcon>
            <Refresh />
          </ListItemIcon>
          <ListItemText>Refresh Data</ListItemText>
        </MenuItem>
        
        <Divider sx={{ backgroundColor: '#4A5568' }} />
        
        <MenuItem onClick={handleDuplicate}>
          <ListItemIcon>
            <ContentCopy />
          </ListItemIcon>
          <ListItemText>Duplicate Widget</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleDelete} sx={{ color: '#F56565' }}>
          <ListItemIcon>
            <Delete sx={{ color: '#F56565' }} />
          </ListItemIcon>
          <ListItemText>Delete Widget</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default WidgetContainer;